import classNames from 'classnames';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { smoothScrollTo } from 'utils/smoothScrollTo';
import { useSearch } from './SearchContext';

interface Props {
  id: string;
  children: ReactNode;
  className?: string;
  wrapperClassName?: string;
  searchTerms?: string[];
  highlightOn?: string[];
}

export const SearchableSection = (props: Props) => {
  const [hihglighted, setHighlighted] = useState(false);
  const [parentPadding, setParentPadding] = useState(0);
  const [parentBorderRadius, setParentBorderRadius] = useState('0');
  const containerRef = useRef<HTMLDivElement>(null);
  const { selectedId, setSelectedId } = useSearch();

  useEffect(() => {
    if (containerRef.current?.parentElement) {
      const parent = containerRef.current.parentElement;
      const paddingLeft = window.getComputedStyle(parent).paddingLeft;
      const paddingValue = Number.parseFloat(paddingLeft);
      const borderRadius = window.getComputedStyle(parent).borderRadius;
      setParentPadding(paddingValue);
      setParentBorderRadius(borderRadius);
    }
  }, []);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const matchedId = selectedId === props.id;
    if (
      selectedId !== undefined &&
      (matchedId || (!matchedId && props.highlightOn?.includes(selectedId)))
    ) {
      setHighlighted(true);
      if (matchedId && containerRef.current) {
        const element = containerRef.current;
        smoothScrollTo(element);
      }
      timeout = setTimeout(() => {
        setSelectedId(undefined);
        setHighlighted(false);
      }, 2000);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [selectedId, setSelectedId, props.id, props.highlightOn]);

  return (
    <div
      className={classNames('relative', props.wrapperClassName)}
      id={props.id}
      ref={containerRef}
    >
      <div className={classNames('relative z-popup', props.className)}>
        {props.children}
      </div>
      <div
        className={classNames(
          'transition-colors duration-500 ease-in-out absolute -inset-y-0 z-0',
          hihglighted && 'bg-blue-50',
        )}
        style={{
          left: `-${parentPadding}px`,
          right: `-${parentPadding}px`,
          borderRadius: parentBorderRadius,
        }}
      />
    </div>
  );
};

// Search functionality depends on displayName of SearchableSection
// by setting it explicitly we ensure it won't get minified
SearchableSection.displayName = 'SearchableSection';
